import { useMemo, useState } from 'react'
import { HiOutlineXMark } from 'react-icons/hi2'
import { resolveIcon } from './LeadForm/iconResolver'

// ────────────────────────────────────────────────────────────────
// IconPicker — curated grid of react-icons names so managers never
// have to type "FaUserTie". The catalog below is hand-picked for
// typical event-form use (roles, industries, timelines, docs, etc.)
// and can grow over time without any schema or backend change —
// all icons resolve through the same resolveIcon() used by the
// public lead form.
//
// The stored value is still the icon's string name, which keeps the
// DB shape identical to what ActiveAdmin writes. Managers pick; the
// lead form renders.
// ────────────────────────────────────────────────────────────────

type CatalogEntry = {
  name: string
  tags: string // lowercased search tags (name + synonyms)
}

// Helper so the table below stays compact.
const e = (name: string, tags: string): CatalogEntry => ({
  name,
  tags: `${name.toLowerCase()} ${tags.toLowerCase()}`,
})

const CATALOG: CatalogEntry[] = [
  // People / roles
  e('FaUserTie', 'person decision maker exec executive leader ceo manager role'),
  e('FaUser', 'person user generic'),
  e('FaUsers', 'team group people'),
  e('FaUserGear', 'technical engineer operator'),
  e('FaUserGraduate', 'student academic graduate'),
  e('FaUserDoctor', 'doctor medical physician health'),
  e('FaUserShield', 'security compliance privacy'),
  e('FaHandshake', 'partner distributor deal agreement'),
  e('FaPeopleGroup', 'team crowd audience'),

  // Industries / work
  e('FaIndustry', 'industry factory manufacturing plant'),
  e('FaBuilding', 'company office corporate building'),
  e('MdCorporateFare', 'company enterprise corporate organization'),
  e('FaStore', 'retail shop store'),
  e('FaShop', 'shop small business retail'),
  e('FaSchool', 'school education academic institution'),
  e('FaHospital', 'hospital clinic medical health'),
  e('FaFlask', 'research lab experiment science'),
  e('FaMicroscope', 'research science lab biology'),
  e('FaLeaf', 'green sustainability eco environment'),
  e('FaBolt', 'energy power fast electric'),
  e('FaGears', 'operations engineering mechanical'),
  e('FaScrewdriverWrench', 'technical tool maintenance fix'),
  e('FaRobot', 'automation ai robot bot'),
  e('FaMicrochip', 'tech hardware chip electronics'),
  e('FaCode', 'developer coding software engineering'),
  e('FaCloud', 'cloud saas infrastructure storage'),
  e('FaServer', 'server datacenter it infrastructure'),
  e('FaDatabase', 'data storage sql'),
  e('FaMobileScreen', 'mobile phone app device'),
  e('FaDesktop', 'computer desktop pc'),

  // Commerce / finance
  e('FaCartShopping', 'procurement shopping cart buy purchase'),
  e('FaCreditCard', 'payment billing card finance'),
  e('FaMoneyBill', 'budget money cash finance'),
  e('FaSackDollar', 'budget money funding revenue'),
  e('FaChartLine', 'growth analytics trend performance'),
  e('FaChartBar', 'analytics stats report'),
  e('FaChartPie', 'share distribution analytics'),
  e('FaBullseye', 'goal target objective'),

  // Communication / docs
  e('FaEnvelope', 'email mail contact message'),
  e('FaPhone', 'phone call contact'),
  e('FaComment', 'message chat feedback'),
  e('FaComments', 'chat conversation discussion'),
  e('FaBullhorn', 'marketing announcement broadcast'),
  e('FaBook', 'guide reference manual'),
  e('FaBookOpen', 'reading learning content'),
  e('FaFileLines', 'document file paper report'),
  e('FaFilePdf', 'pdf document download'),
  e('FaClipboardList', 'list checklist survey'),
  e('FaClipboardCheck', 'approval verified check'),
  e('MdChromeReaderMode', 'catalog document reading brochure'),
  e('MdBuild', 'solutions build custom toolkit'),

  // Time / planning
  e('HiCalendar', 'calendar date schedule timeline'),
  e('HiCalendarDays', 'calendar days month schedule'),
  e('FaClock', 'time clock schedule deadline'),
  e('FaHourglassHalf', 'waiting in progress duration'),
  e('HiArrowTrendingUp', 'growth upward trend'),
  e('HiArrowTrendingDown', 'decline downward trend'),

  // Discovery / status
  e('FaMagnifyingGlass', 'search research benchmarking lookup'),
  e('FaBinoculars', 'exploring scouting observe'),
  e('FaCompass', 'direction navigate discovery'),
  e('FaMapLocation', 'location map geography'),
  e('FaMapPin', 'location place pin'),
  e('FaGlobe', 'global international worldwide'),

  // Decision / check
  e('FaCircleCheck', 'confirmed yes approved done'),
  e('FaCircleXmark', 'no rejected cancel remove'),
  e('FaCircleQuestion', 'unsure question help maybe'),
  e('FaThumbsUp', 'like approve positive'),
  e('FaThumbsDown', 'dislike reject negative'),
  e('FaStar', 'favorite featured highlight'),
  e('FaHeart', 'favorite love interested'),
  e('FaFire', 'hot trending priority'),
  e('FaLightbulb', 'idea insight innovation'),
  e('FaRocket', 'launch growth startup'),
  e('FaGem', 'premium valuable quality'),
  e('FaTrophy', 'award winner achievement'),
  e('FaMedal', 'award recognition prize'),
  e('FaCrown', 'premium top vip'),

  // Misc
  e('FaBriefcase', 'work business job career'),
  e('FaTruckFast', 'delivery logistics shipping'),
  e('FaBoxesStacked', 'inventory supply warehouse'),
  e('FaKey', 'access security auth'),
  e('FaLock', 'secure private locked'),
  e('FaTag', 'tag label category'),
  e('FaGift', 'gift bonus reward'),
]

type Props = {
  value: string
  onChange: (next: string) => void
}

export default function IconPicker({ value, onChange }: Props) {
  const [open, setOpen] = useState(false)

  const preview = resolveIcon(value, 'h-5 w-5')

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-9 w-12 shrink-0 items-center justify-center gap-1 rounded-md border border-gray-300 bg-white px-2 text-gray-600 shadow-sm transition hover:bg-gray-50"
        aria-label="Pick icon"
        title={value || 'Pick icon'}
      >
        {preview ? (
          preview
        ) : (
          <span className="font-display text-xs text-black/40">Icon</span>
        )}
      </button>
      {open && (
        <IconPickerDialog
          current={value}
          onPick={(name) => {
            onChange(name)
            setOpen(false)
          }}
          onClear={() => {
            onChange('')
            setOpen(false)
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  )
}

function IconPickerDialog({
  current,
  onPick,
  onClear,
  onClose,
}: {
  current: string
  onPick: (name: string) => void
  onClear: () => void
  onClose: () => void
}) {
  const [q, setQ] = useState('')
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return CATALOG
    return CATALOG.filter((entry) => entry.tags.includes(needle))
  }, [q])

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-16 font-sans"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h2 className="font-display text-lg font-semibold text-midnight-green">Pick an icon</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <HiOutlineXMark className="h-5 w-5" />
          </button>
        </div>
        <div className="px-6 py-4">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search: doctor, company, budget…"
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 font-display text-sm text-ink shadow-sm focus:border-midnight-green focus:outline-none focus:ring-1 focus:ring-midnight-green"
            autoFocus
          />
        </div>
        <div className="max-h-96 overflow-y-auto px-4 pb-4">
          {filtered.length === 0 ? (
            <p className="px-4 py-6 text-center font-display text-sm text-black/50">
              No icons match. Try a different word.
            </p>
          ) : (
            <div className="grid grid-cols-6 gap-2">
              {filtered.map((entry) => {
                const node = resolveIcon(entry.name, 'h-6 w-6')
                const isCurrent = entry.name === current
                return (
                  <button
                    key={entry.name}
                    type="button"
                    onClick={() => onPick(entry.name)}
                    className={`flex aspect-square flex-col items-center justify-center rounded-md border p-1 transition ${
                      isCurrent
                        ? 'border-midnight-green bg-[#93D500]/20 text-midnight-green'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    title={entry.name}
                  >
                    {node}
                  </button>
                )
              })}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-6 py-3">
          <button
            type="button"
            onClick={onClear}
            className="font-display text-sm text-black/60 transition hover:text-rose-600"
          >
            Clear icon
          </button>
          <span className="font-display text-xs text-black/40">
            {filtered.length} of {CATALOG.length}
          </span>
        </div>
      </div>
    </div>
  )
}
